import { SearchOutlined } from "@ant-design/icons"
import { Flex, Form, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { LeadWizardCustomerRow } from "./LeadWizardCustomerRow.tsx"
import {
  type CustomerMatchCandidate,
  candidateKey,
  fromSearchResult
} from "./customer-match.ts"
import { useCustomerSearch } from "./use-customer-search.ts"
import { TextInput } from "../../../../components/Input/TextInput.tsx"

const VISIBLE_RESULTS = 5
export const CUSTOMER_SEARCH_INPUT_ID = "lead-wizard-customer-search"

interface LeadWizardCustomerSearchProps {
  onUse: (candidate: CustomerMatchCandidate) => void
  busyKey?: string | null
  disabled?: boolean
}

export function LeadWizardCustomerSearch({
  onUse,
  busyKey,
  disabled
}: LeadWizardCustomerSearchProps) {
  const { t } = useTranslation()
  const { query, results, loading, search, clear } = useCustomerSearch()

  const candidates = results
    .slice(0, VISIBLE_RESULTS)
    .map((result) => fromSearchResult(result, {}))

  const applyCustomer = (candidate: CustomerMatchCandidate) => {
    clear()
    onUse(candidate)
  }

  return (
    <Form.Item
      // The control is outside the form's value plumbing, so antd wires no id.
      htmlFor={CUSTOMER_SEARCH_INPUT_ID}
      label={t("leadWizard.search.label")}
    >
      <TextInput
        disableAutoCapitalize
        allowClear
        id={CUSTOMER_SEARCH_INPUT_ID}
        disabled={disabled}
        value={query}
        prefix={<SearchOutlined />}
        placeholder={t("leadWizard.search.placeholder")}
        onChange={(event) => search(event.target.value)}
      />

      {loading && (
        <Typography.Text type={"secondary"} className={"text-xs"}>
          {t("leadWizard.search.searching")}
        </Typography.Text>
      )}

      {!loading && !!query.trim() && !candidates.length && (
        <Typography.Text type={"secondary"} className={"text-xs"}>
          {t("leadWizard.search.notFound")}
        </Typography.Text>
      )}

      {!!candidates.length && (
        <Flex vertical gap={"small"} className={"mt-2"}>
          {candidates.map((candidate) => (
            <LeadWizardCustomerRow
              key={candidateKey(candidate)}
              candidate={candidate}
              busy={busyKey === candidateKey(candidate)}
              actionLabel={t("leadWizard.search.use")}
              onUse={() => applyCustomer(candidate)}
            />
          ))}
        </Flex>
      )}
    </Form.Item>
  )
}
